import { useEffect, useState, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import type { AxiosError } from 'axios'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { Select } from '@components/shared/Select'
import { Input } from '@components/shared/Input'
import { DatePicker } from '@components/shared/DatePicker'
import { Switch } from '@components/shared/Switch'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { useUser } from '@hooks/useUser'
import { usePeople } from '@pages/People/context'
import { parseCompetenceDate } from '@utils/dates/parseCompetenceDate'
import { startOfMonth, endOfMonth } from 'date-fns'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import * as apiPeople from '@services/api/people'

import type { NewSimulationModalProps } from './types'
import { newSimulationModalSchema } from './form/validations'
import { initialFormValues } from './form/initialFormValues'
import type { NewSimulationFormProps } from './form/types'
import type { SimulationCollaboratorProps } from '@pages/People/context/types'
import * as S from './styles'

export const NewSimulationModal = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  collaboratorFilters,
  canEdit = true
}: NewSimulationModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const { createAdmissionSimulation } = usePeople()
  const [collaborators, setCollaborators] = useState<SimulationCollaboratorProps[]>([])
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false)
  const [isSubmittingAdmission, setIsSubmittingAdmission] = useState(false)

  const methods = useForm<NewSimulationFormProps>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: newSimulationModalSchema as any,
    defaultValues: initialFormValues
  })

  const simulationType = methods.watch('simulationType')
  const salarioAdmissao = methods.watch('salarioAdmissao')
  const dataInicioAdmissao = methods.watch('dataInicioAdmissao')

  // Mês do referenceDate (filiData.startPeriod) para restringir o DatePicker apenas a esse mês
  const referenceMonthRange = useMemo(() => {
    if (!filiData?.startPeriod) return null
    const refDate = parseCompetenceDate(filiData.startPeriod)
    if (!refDate) return null
    return {
      min: startOfMonth(refDate),
      max: endOfMonth(refDate),
      openTo: startOfMonth(refDate)
    }
  }, [filiData?.startPeriod])

  // Calcular salário proporcional para admissão
  const [proportionalSalary, setProportionalSalary] = useState<number | null>(null)
  const [isCalculatingSalary, setIsCalculatingSalary] = useState(false)

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      setProportionalSalary(null)
    }
  }, [open, methods])

  useEffect(() => {
    const fetchCollaborators = async () => {
      if (!open || !filiData?.filiCode || !filiData?.startPeriod || simulationType !== 'RESCISAO') return

      const referenceDate =
        filiData.startPeriod.match(/^\d{4}-\d{2}-\d{2}$/) ?
          filiData.startPeriod
          : (convertDateToIsoDate(filiData.startPeriod) ?? filiData.startPeriod)
      setIsLoadingCollaborators(true)

      try {
        if (collaboratorFilters) {

          const params: Parameters<typeof apiPeople.getCollaboratorsList>[0] = {
            filiCode: filiData.filiCode,
            referenceDate,
            currentPage: 1,
            itemsPerPage: 1000
          }
          if (collaboratorFilters.name) params.employeeName = collaboratorFilters.name
          if (collaboratorFilters.matricula) params.employeeRegistration = collaboratorFilters.matricula
          if (collaboratorFilters.cargo?.length) params.positions = collaboratorFilters.cargo
          if (collaboratorFilters.status?.length) params.statuses = collaboratorFilters.status
          if (collaboratorFilters.dataInicio) {
            const startDateIso = convertDateToIsoDate(collaboratorFilters.dataInicio)
            if (startDateIso) params.startDateFrom = `${startDateIso}T00:00:00`
          }

          const response = await apiPeople.getCollaboratorsList({ ...params, excludeWithTermination: true })

          const mapped: SimulationCollaboratorProps[] = (response?.items ?? []).map((item) => ({
            employeeName: item.employeeName,
            employeeRegistration: item.employeeRegistration,
            position: item.position,
            startDate: item.startDate,
            status: item.status,
            salary: item.salaries ?? 0
          }))

          setCollaborators(mapped)
        } else {
          const data = await apiPeople.getSimulationCollaborators(
            filiData.filiCode,
            referenceDate
          )
          setCollaborators(data || [])
        }
      } catch (error) {
        console.error('Erro ao buscar colaboradores:', error)
        setCollaborators([])
      } finally {
        setIsLoadingCollaborators(false)
      }
    }

    fetchCollaborators()
  }, [open, filiData?.filiCode, filiData?.startPeriod, simulationType, collaboratorFilters])

  // Calcular salário proporcional para admissão
  useEffect(() => {
    if (simulationType !== 'ADMISSAO' || !salarioAdmissao || !dataInicioAdmissao || !filiData?.filiCode) {
      setProportionalSalary(null)
      return
    }

    const salario = unMaskCurrency(salarioAdmissao)
    if (!salario || salario <= 0) {
      setProportionalSalary(null)
      return
    }

    // Converter data de dd/MM/yyyy para ISO
    const convertDateToIso = (dateStr: string): string | null => {
      if (!dateStr) return null
      if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateStr.split('/')
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`
      }
      return null
    }

    const startDate = convertDateToIso(dataInicioAdmissao)
    if (!startDate) {
      setProportionalSalary(null)
      return
    }

    setIsCalculatingSalary(true)

    apiPeople.calculateProportionalSalaryAdmission(filiData.filiCode, {
      salary: salario,
      startDate
    })
      .then((response) => {
        setProportionalSalary(response.proportionalSalary)
      })
      .catch((error) => {
        console.error('Erro ao calcular salário proporcional:', error)
        setProportionalSalary(null)
      })
      .finally(() => {
        setIsCalculatingSalary(false)
      })
  }, [salarioAdmissao, dataInicioAdmissao, simulationType, filiData?.filiCode])

  // Converter colaboradores para formato de options
  const collaboratorOptions = useMemo(() => {
    return collaborators.map((collaborator) => ({
      value: collaborator.employeeRegistration,
      label: collaborator.employeeName
    }))
  }, [collaborators])

  const convertDateToIsoDate = (dateStr: string): string | null => {
    if (!dateStr) return null
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr
    if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateStr.split('/')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
    return null
  }

  const onSubmit = async () => {
    if (!canEdit) return

    const formData = methods.getValues()

    if (formData.simulationType === 'RESCISAO') {
      const selectedCollaborator = collaborators.find(
        (collab) => collab.employeeRegistration === formData.nome
      )
      onConfirm(formData.simulationType, selectedCollaborator || null)
      onClose(false)
      return
    }

    if (formData.simulationType === 'ADMISSAO') {
      if (!filiData?.filiCode || !filiData?.startPeriod) return

      const startDate = convertDateToIsoDate(formData.dataInicioAdmissao)
      if (!startDate) return

      let referenceDate = filiData.startPeriod
      if (referenceDate && !referenceDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        referenceDate = convertDateToIsoDate(referenceDate) || referenceDate
      }
      const salary = unMaskCurrency(formData.salarioAdmissao)
      if (salary <= 0) {
        methods.setError('salarioAdmissao', {
          type: 'manual',
          message: 'O salário deve ser maior que zero.'
        })
        return
      }

      setIsSubmittingAdmission(true)
      try {
        await createAdmissionSimulation(filiData.filiCode, {
          position: formData.cargo,
          startDate,
          salary,
          referenceDate
        })
        const admissionCollaborator: SimulationCollaboratorProps = {
          employeeName: formData.cargo,
          employeeRegistration: 'NEW',
          position: formData.cargo,
          startDate: formData.dataInicioAdmissao,
          status: 'Novo',
          salary
        }
        onConfirm(formData.simulationType, admissionCollaborator)
        onClose(false)
      } catch (err) {
        const axiosError = err as AxiosError<{ errors?: Record<string, string[]> }>
        const apiErrors = axiosError?.response?.data?.errors
        if (axiosError?.response?.status === 400 && apiErrors) {
          const fieldMap: Record<string, keyof NewSimulationFormProps> = {
            Salary: 'salarioAdmissao',
            Position: 'cargo',
            StartDate: 'dataInicioAdmissao'
          }
          for (const [apiField, messages] of Object.entries(apiErrors)) {
            const formField = fieldMap[apiField]
            const message = Array.isArray(messages) ? messages[0] : messages
            if (formField && message) {
              methods.setError(formField, { type: 'server', message })
            }
          }
        }
      } finally {
        setIsSubmittingAdmission(false)
      }
    }
  }

  return (
    <BaseModal2
      open={open}
      title="Nova Simulação"
      onClose={onClose}
      width={isMobile ? 'calc(100% - 20px)' : 500}
      heightContainer="auto"
      maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
      footer={{
        cancelButtonText: 'Cancelar',
        cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
        confirmButtonText: canEdit ? 'Confirmar' : undefined,
        confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
        onConfirm: canEdit ? () => methods.handleSubmit(onSubmit)() : undefined,
        loading: isLoading || isSubmittingAdmission,
        confirmButtonDisabled: isSubmittingAdmission,
        actionsAlign: 'center'
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <S.FormContainer>
            <S.WarningBanner>
              <WarningIcon />
              <S.WarningText>
                Esta Admissão/Rescisão é uma simulação para prévia. A efetivação depende do registro no sistema oficial (EPM/SAP).
              </S.WarningText>
            </S.WarningBanner>

            <Select
              required
              name="simulationType"
              label="Tipo de Simulação"
              placeholder="Selecione o tipo de simulação"
              options={[
                { value: 'ADMISSAO', label: 'Admissão' },
                { value: 'RESCISAO', label: 'Rescisão' }
              ]}
              disabled={!canEdit}
            />

            {simulationType === 'RESCISAO' && (
              <>
                <Select
                  name="nome"
                  label="Nome"
                  placeholder="Selecione o nome do colaborador"
                  required
                  options={collaboratorOptions}
                  loading={isLoadingCollaborators}
                  disabled={!canEdit}
                />

                <S.SwitchContainer>
                  <Switch
                    name="semJustaCausa"
                    label="Sem Justa Causa"
                    disabled={true}
                  />
                </S.SwitchContainer>
              </>
            )}

            {simulationType === 'ADMISSAO' && (
              <>
                <Input
                  name="cargo"
                  label="Cargo"
                  placeholder="Insira o cargo do colaborador."
                  required
                  disabled={!canEdit}
                />

                <DatePicker
                  name="dataInicioAdmissao"
                  label="Data Prevista Início"
                  placeholder="Selecione a data"
                  required
                  minDate={referenceMonthRange?.min}
                  maxDate={referenceMonthRange?.max}
                  openToDate={referenceMonthRange?.openTo}
                  disabled={!canEdit}
                />

                <Input
                  name="salarioAdmissao"
                  label="Salário"
                  placeholder="Insira o salário do colaborador."
                  required
                  maskType="currency"
                  disabled={!canEdit}
                />


                <S.ProportionalSalaryContainer>
                  <Input
                    hasController={false}
                    name="salarioProporcional"
                    label="Salário Proporcional"
                    value={
                      isCalculatingSalary
                        ? '...'
                        : proportionalSalary !== null
                          ? new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          }).format(proportionalSalary)
                          : 'Será calculado automaticamente'
                    }
                    disabled
                    tooltipInfo="Cálculo: Salário base ÷ 30 × quantidade de dias restantes no mês."
                  />
                </S.ProportionalSalaryContainer>
              </>
            )}
          </S.FormContainer>
        </form>
      </FormProvider>
    </BaseModal2>
  )
}
